import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { createMuiTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

const ThemeContext = createContext();

// Identidade Allarmi (allarmi.com.br): verde-escuro #011B01, verde #85C440, verde-limão do símbolo #87EF15.
export const MARCA = {
  escuro: "#011B01",
  verde: "#85C440",
  limao: "#87EF15",
  verdeClaro: "#DCEDC9",
  verdeForte: "#3B7A14", // verde da marca escurecido: contraste com texto branco nos botões
};

const lerModo = () => {
  try {
    return localStorage.getItem("modoEscuro") === "1";
  } catch {
    return false;
  }
};

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(lerModo);

  const toggleTheme = () => {
    setDarkMode((prevMode) => {
      try {
        localStorage.setItem("modoEscuro", prevMode ? "0" : "1");
      } catch {
        // sem armazenamento: só não lembra a escolha
      }
      return !prevMode;
    });
  };

  const theme = useMemo(
    () =>
      createMuiTheme({
        palette: darkMode
          ? {
              type: "dark",
              primary: { main: MARCA.verde, contrastText: MARCA.escuro },
              secondary: { main: MARCA.limao, contrastText: MARCA.escuro },
              background: { default: "#0d130b", paper: "#151d12" },
            }
          : {
              type: "light",
              primary: { main: MARCA.verdeForte, dark: MARCA.escuro, light: MARCA.verde, contrastText: "#fff" },
              secondary: { main: MARCA.verde, dark: "#5e9a22", contrastText: MARCA.escuro },
              background: { default: "#f4f6f1", paper: "#ffffff" },
              text: { primary: "#1b2416", secondary: "#5b6656" },
            },
        typography: {
          fontFamily: '"Plus Jakarta Sans", "Roboto", "Segoe UI", sans-serif',
          button: { textTransform: "none", fontWeight: 600 },
        },
        shape: { borderRadius: 8 },
        scrollbarStyles: {
          "&::-webkit-scrollbar": { width: "8px", height: "8px" },
          "&::-webkit-scrollbar-thumb": {
            boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
            backgroundColor: darkMode ? "#2a3524" : "#e2e7dc",
          },
        },
        overrides: {
          MuiAppBar: { colorPrimary: { backgroundColor: MARCA.escuro } },
        },
      }),
    [darkMode]
  );

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
